import React, { useState } from 'react'
import Linking from 'expo-linking/build/ExpoLinking'

import { XStack, View, useMedia } from 'tamagui'

import { Paragraph } from '../Text'
import { Tooltip } from '../Tooltip'
import { IconWrapper } from '../Icon/IconWrapper'

import BlueskyIcon from '../Icon/icons/BlueskyIcon'
import GitHubIcon from '../Icon/icons/GitHubIcon'
import InstagramIcon from '../Icon/icons/InstagramIcon'
import LinkedInIcon from '../Icon/icons/LinkedInIcon'
import TwitterIcon from '../Icon/icons/TwitterIcon'
import WebsiteIcon from '../Icon/icons/WebsiteIcon'
import WindowNew from '../Icon/icons/WindowNew'
import YouTubeIcon from '../Icon/icons/YouTubeIcon'

export type SocialFields = {
  websiteUrl?: string | null
  twitterUrl?: string | null
  instagramUrl?: string | null
  linkedinUrl?: string | null
  youtubeUrl?: string | null
  githubUrl?: string | null
  blueskyUrl?: string | null
  blueskyIdentifier?: string | null
}

type SocialLink = {
  url: string
  label: string
  icon: React.ReactNode
}

export function buildSocialLinks(data: SocialFields): SocialLink[] {
  const links: SocialLink[] = []

  if (data.websiteUrl) {
    links.push({ url: data.websiteUrl, label: 'Website', icon: <WebsiteIcon /> })
  }
  const blueskyUrl = data.blueskyUrl || (data.blueskyIdentifier ? `https://bsky.app/profile/${data.blueskyIdentifier}` : null)
  if (blueskyUrl) {
    links.push({
      url: blueskyUrl,
      label: 'Bluesky',
      icon: <BlueskyIcon />,
    })
  }
  if (data.twitterUrl) {
    links.push({ url: data.twitterUrl, label: 'Twitter', icon: <TwitterIcon /> })
  }
  if (data.githubUrl) {
    links.push({ url: data.githubUrl, label: 'GitHub', icon: <GitHubIcon /> })
  }
  if (data.linkedinUrl) {
    links.push({ url: data.linkedinUrl, label: 'LinkedIn', icon: <LinkedInIcon /> })
  }
  if (data.instagramUrl) {
    links.push({ url: data.instagramUrl, label: 'Instagram', icon: <InstagramIcon /> })
  }
  if (data.youtubeUrl) {
    links.push({ url: data.youtubeUrl, label: 'YouTube', icon: <YouTubeIcon /> })
  }

  return links
}

const SocialIconLink = ({
  href,
  label,
  icon,
  size = '$4',
}: {
  href: string
  label: string
  icon: React.ReactNode
  size?: '$4' | '$5'
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const { reducedMotion: reducedEffects } = useMedia()

  // Use a View instead of ExternalLink (<a> tag) to avoid invalid nested
  // anchor elements when rendered inside a Link-wrapped card.
  return (
    <Tooltip placement='top' delay={100}>
      <Tooltip.Trigger tag='dialog'>
        <View
          role='link'
          aria-label={label}
          cursor='pointer'
          onHoverIn={() => setIsHovered(true)}
          onHoverOut={() => setIsHovered(false)}
          onPress={(e: any) => {
            e.stopPropagation()
            e.preventDefault?.()
            Linking.openURL(href)
          }}
          width={size}
          height={size}
        >
          <XStack position='absolute'>
            <IconWrapper size={size} color='$colorMuted'>
              {icon}
            </IconWrapper>
          </XStack>

          <XStack
            animation='fastSpring'
            opacity={isHovered ? 1 : 0}
            position='absolute'
          >
            <IconWrapper size={size} color='$primaryColor'>
              {icon}
            </IconWrapper>
          </XStack>
        </View>
      </Tooltip.Trigger>

      <Tooltip.Content
        animation='fastSpring'
        enterStyle={{ x: 0, y: 5, opacity: 0, scale: 0.9 }}
        exitStyle={{ x: 0, y: 5, opacity: 0, scale: 0.9 }}
        scale={1}
        x={0}
        y={0}
        opacity={1}
        borderColor='rgba(105, 102, 118, 1.0)'
        backgroundColor='rgba(105, 102, 118, 0.8)'
        style={reducedEffects ? {} : {
          backdropFilter: 'blur(8px) saturate(250%)',
          WebkitBackdropFilter: 'blur(8px) saturate(250%)',
        }}
      >
        <Tooltip.Arrow
          borderColor='rgba(105, 102, 118, 1.0)'
          backgroundColor='rgba(105, 102, 118, 0.7)'
          style={reducedEffects ? {} : {
            backdropFilter: 'blur(8px) saturate(250%)',
            WebkitBackdropFilter: 'blur(8px) saturate(250%)',
          }}
        />

        <XStack gap='$1'>
          <IconWrapper size='$4' color='$colorOnPrimary'>
            <WindowNew />
          </IconWrapper>

          <Paragraph size='$4' color='$colorOnPrimary' fontWeight='$5'>
            {label}
          </Paragraph>
        </XStack>
      </Tooltip.Content>
    </Tooltip>
  )
}

export const SocialProfileLinksRow = ({
  links,
  size = '$4',
}: {
  links: SocialLink[]
  size?: '$4' | '$5'
}) => {
  if (links.length === 0) return null

  return (
    <XStack
      gap='$3'
      alignItems='center'
      flexWrap='wrap'
      // Stop click events from bubbling to parent card wrappers that use
      // onPress for navigation. Without this, clicking a social link
      // inside a UserCard/GuildCard also navigates to the profile page.
      onPress={(e: any) => e.stopPropagation()}
    >
      {links.map((link) => (
        <SocialIconLink
          key={link.label}
          href={link.url}
          label={link.label}
          icon={link.icon}
          size={size}
        />
      ))}
    </XStack>
  )
}
